import { useState, useMemo } from "react";
import { TextField, Autocomplete, Box, Chip, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useSearchFootballEntities } from "../../api/v1/queries/useSearchFootballEntities";
import { formatImgUrl } from "../../utils/cardFormating";
import { Entity } from "../../types/types/entity.types";

type EntityAutocompleteProps = {
  multiple?: boolean;
  limitTags?: number;
  value?: Entity | Entity[] | null;
  onChange?: (value: Entity | Entity[] | null) => void;
  error?: boolean;
};

const EntityAutocomplete = ({
  multiple = false,
  limitTags = 2,
  value,
  onChange,
  error,
  ...props
}: EntityAutocompleteProps) => {
  const { i18n } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Fetch initial results when opened, or search results when typing
  const shouldSearch = inputValue.length >= 2;
  const shouldFetchInitial = isOpen && !shouldSearch;

  const { searchFootballEntitiesData, isSearchFootballEntitiesLoading, isSearchFootballEntitiesError } =
    useSearchFootballEntities({
      search: shouldSearch ? inputValue : undefined,
      limit: 50,
    });

  // Get selected entities - value is already Entity or Entity[]
  const selectedEntities = useMemo(() => {
    if (!value) return null;
    if (multiple && Array.isArray(value)) {
      return value;
    }
    if (!multiple && !Array.isArray(value)) {
      return value;
    }
    return null;
  }, [value, multiple]);

  // Combine search results with selected entities to ensure selected ones are always in options
  const entityOptions = useMemo(() => {
    const searchEntities = searchFootballEntitiesData || [];
    const selectedIds = selectedEntities
      ? Array.isArray(selectedEntities)
        ? selectedEntities.map((e) => e.id)
        : [selectedEntities.id]
      : [];

    // Merge search results with selected entities, avoiding duplicates
    const allEntities = [...searchEntities];
    if (selectedEntities) {
      const selectedArray = Array.isArray(selectedEntities) ? selectedEntities : [selectedEntities];
      selectedArray.forEach((selectedEntity) => {
        if (!allEntities.some((e) => e.id === selectedEntity.id)) {
          allEntities.push(selectedEntity);
        }
      });
    }

    return allEntities;
  }, [searchFootballEntitiesData, selectedEntities]);

  // Get label for entity based on locale
  const getEntityLabel = (entity: Entity): string => {
    if (i18n.language === "ar" && entity.name_ar) {
      return entity.name_ar;
    }
    if (i18n.language === "en" && entity.name_en) {
      return entity.name_en;
    }
    return entity.name;
  };

  return (
    <Autocomplete
      {...props}
      multiple={multiple}
      limitTags={limitTags}
      disablePortal
      disabled={isSearchFootballEntitiesLoading}
      loading={isSearchFootballEntitiesLoading}
      options={entityOptions}
      value={selectedEntities}
      inputValue={inputValue}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      onInputChange={(_, newInputValue, reason) => {
        // Allow spaces and all characters - only ignore reset events
        if (reason !== "reset") {
          setInputValue(newInputValue);
        }
      }}
      selectOnFocus={false}
      clearOnBlur={false}
      onChange={(_, newValue) => {
        onChange?.(newValue as Entity | Entity[] | null);
        // Clear input after selection in multiple mode
        if (multiple) {
          setInputValue("");
        }
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        return getEntityLabel(option);
      }}
      filterOptions={(options) => options} // Disable client-side filtering, we handle it server-side
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isSearchFootballEntitiesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option: Entity) => (
        <Box component="li" {...props} sx={styles.option}>
          {option.mediaUrl && (
            <Box
              component="img"
              src={formatImgUrl(option.mediaUrl)}
              alt={getEntityLabel(option)}
              sx={styles.entityLogo}
              onError={(e) => {
                // Hide image on error
                e.currentTarget.style.display = "none";
              }}
            />
          )}
          {getEntityLabel(option)}
        </Box>
      )}
      renderTags={(values, getTagProps) =>
        values.map((option, index) => (
          <Chip
            sx={styles.autocompleteChip}
            label={
              <Box component="span" sx={styles.autocompleteChipLabel}>
                {option.mediaUrl && (
                  <Box
                    component="img"
                    src={formatImgUrl(option.mediaUrl)}
                    alt={getEntityLabel(option)}
                    sx={styles.entityLogoSmall}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
                {getEntityLabel(option)}
              </Box>
            }
            {...getTagProps({ index })}
          />
        ))
      }
      sx={styles.autocomplete}
      noOptionsText={
        isOpen && !shouldSearch
          ? "Loading entities..."
          : inputValue.length < 2
          ? "Type at least 2 characters to search"
          : "No entities found"
      }
    />
  );
};

export default EntityAutocomplete;

const styles = {
  autocomplete: {
    "& .MuiAutocomplete-input": {
      padding: "0 !important",
    },
  },
  entityLogo: {
    width: "32px",
    height: "32px",
    borderRadius: "4px",
    objectFit: "cover",
  },
  entityLogoSmall: {
    width: "20px",
    height: "20px",
    borderRadius: "4px",
    objectFit: "cover",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  autocompleteChipLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    maxWidth: "100px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  autocompleteChip: {
    borderRadius: "6px",
    margin: "0 !important",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    background: "var(--Base-White, #FFF)",
    gap: "4px",
    padding: "2px 5px",

    "& .MuiChip-label": {
      padding: 0,
    },
    "& .MuiChip-deleteIcon": {
      margin: 0,
    },
  },
};
